/** /tours/ — cross-tenant Group_Tour catalog (Phase 1: list + retrieve).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Create/update/delete land in later phases.
 */
import { apiDelete, apiGet, apiPatch, apiPost, client, extendedListConfig } from './client';
import type { ExtIdType, ExtIdUsedBy } from './extIds';
import type { ImportRunListItem } from './importRuns';
import type { LaunchPolicyFlags } from './launchPolicies';
import type { PaginatedResponse } from './users';
import type { components } from './types';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface TenantRef {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  parent_id: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface NamedRef {
  id: number;
  name: string | null;
}

/** Group_Tour.trip_type — content discriminator. */
export const TRIP_TYPE = { TOUR: 0, BUS_TRIP: 1, EXHIBITION: 2 } as const;
export type TripType = (typeof TRIP_TYPE)[keyof typeof TRIP_TYPE];

export const TRIP_TYPE_LABELS: Record<number, string> = {
  0: 'Tour',
  1: 'Bus trip',
  2: 'Exhibition',
};

export interface TourListItem {
  id: number;
  /** Owning tenant; null for unassigned/orphaned tours (`Group_Tour.tour_operator`
   *  is nullable). The list grid renders null as '—'. */
  tenant: TenantRef | null;
  name: string;
  trip_type: number;
  trip_type_display: string;
  is_bespoke: boolean;
  use_as_template: boolean;
  start_date: string | null;
  has_departures: boolean;
  custom_id: string | null;
  is_import_bound: boolean;
  num_bookings: number;
  /** Own tags (visible chips). */
  tags: NamedRef[];
  /** Effective launch policy ref (visible). */
  launch_policy: NamedRef | null;
  /** Fixed-date start instant (hidden by default). */
  start_datetime: string | null;
  start_timezone: string | null;
  /** Fixed-date end (hidden by default). */
  end_date: string | null;
  /** Whether guests may self-register into this tour (hidden by default). */
  allow_selfreg: boolean;
  is_deleted: boolean;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface ExternalLink {
  id: number;
  name: string | null;
  url: string | null;
  deep_link: boolean;
}

/** A tag a participant gets when a booking matches through an alias. */
export interface AliasTag {
  id: number;
  name: string | null;
  /** The tag's category code. */
  category: string | null;
}

/** Another tour a booking matching an alias is also added to. */
export interface AliasTarget {
  id: number;
  /** Null when the target tour no longer exists. */
  name: string | null;
  is_deleted: boolean;
}

/** One alias (Additional_Ext_ID) of a tour — ConsoleTourAlias. */
export interface AdditionalCustomId {
  id: number;
  custom_id: string | null;
  id_type: ExtIdType | string;
  /** Off = parked: booking sync and the importer skip it. */
  use_for_matching: boolean;
  api_queryable: boolean;
  /** The restech code the alias is scoped to (`bokun`); null = any. */
  integration: string | null;
  /** The restech account slug; null = the default account. */
  restech_account: string | null;
  comment: string | null;
  ext_link: string | null;
  /** Import-owned: the import that created it (`integration`; null = a direct XML
   *  import) updates it on every sync, so the editor holds it read-only; the PATCH
   *  applies only this flag to a managed row. false = ops-owned, never touched by an
   *  import. Flipped either way through the list (take over / hand back). */
  managed: boolean;
  tour_user_tags: AliasTag[];
  targets: AliasTarget[];
  /** Read-only: which pipelines consume it (the ext-IDs index facets). */
  used_by: ExtIdUsedBy;
}

export interface MeetingPoint {
  id: number;
  name: string | null;
  address: string | null;
  comment: string | null;
  latitude: number | null;
  longitude: number | null;
  maps_url: string | null;
  photo: { id: number; url: string | null; p: string | null; s: string | null } | null;
}

export interface ImportSummary {
  is_import_bound: boolean;
  sources: { id: number; code: string | null; name: string | null }[];
  /** The last 20 runs that touched the tour, newest first (the import-runs grid's rows). */
  runs: ImportRunListItem[];
}

export interface Departure {
  id: number;
  tag: { id: number; name: string } | null;
}

/** A Group_Tour_Tag_Category_M2M row (own or inherited). Flags per the data-model spec. */
export interface TagCategoryM2M {
  id: number;
  category: { id: number; name: string | null; code: string | null } | null;
  group_tour: number | null;
  sequence: number;
  min_tags: number;
  max_tags: number;
  user_selected: boolean;
  chat_conversation: boolean;
  visible_in_chat: boolean;
  location_sharing: boolean;
  location_all_if_no_tag: boolean;
  user_association: boolean;
  guides_from_tags: boolean;
  copy_tags_to_guides: boolean;
  copy_tags_to_subbookings: boolean;
  remove_upon_date_changes: boolean;
  checkin: boolean;
  boarding: boolean;
  visible_on_bookingmgmt: boolean;
  cleanup_on_assignment: boolean;
  departure_wide: boolean;
  remove_guides_on_cleanup: boolean;
  checkin_display: boolean;
  remove_unreferenced_guides: boolean;
}

export interface PreTripItemRow {
  id: number;
  item: { id: number; name: string | null } | null;
  sequence: number;
  required: boolean;
  all_users: boolean;
  all_travelers: boolean;
  roles: NamedRef[];
  /** `name`: full name, else the email; null when neither. */
  users: { id: number; name: string | null }[];
  agroups: NamedRef[];
}

/** Pre-check-in settings blob (own or effective). Loosely typed — the editor
 *  reads/writes a known subset; the rest round-trips. */
export type PreCheckSettings = Record<string, unknown>;

export interface PickerOption {
  id: number;
  name: string | null;
  code?: string;
  /** currencies */
  symbol?: string;
  /** transport_types */
  icon_url?: string | null;
  /** launch_policies: the owning tenant (null = a global policy); activities with
   *  `include_parent`: the activity's owner. */
  tenant?: TenantRef | null;
  /** launch_policies: the policy's 12 flags. */
  flags?: LaunchPolicyFlags;
  /** launch_policies: the policy's OWNER enforces launch policies (false = it holds
   *  nothing back). */
  enforced?: boolean;
}

export type PickerKind =
  | 'tag_categories' | 'activities' | 'pre_trip_items' | 'waivers'
  | 'launch_policies' | 'departure_tags' | 'template_tours' | 'route_sets'
  | 'atags' | 'tags' | 'crosssells'
  | 'transport_types' | 'timeline_types' | 'currencies'
  // The pre-trip items' "Visible to" targets: the operator's roles and access groups
  // (`code` = codename), and the users booked on one tour (`tour_users`, `tour_id`; `id` is
  // the user's, `code` the booking's role name — null = a traveler).
  | 'roles' | 'access_groups' | 'tour_users';

/** One account of a booking system the operator has configured (`restech_accounts`
 *  picker) — what an alias's `integration` / `restech_account` may name. */
export interface RestechAccountOption {
  /** `<restech>` (the default account) or `<restech>:<slug>`. */
  id: string;
  /** "Bokun · Default". */
  name: string;
  /** The restech code (`bokun`). */
  code: string;
  /** The account slug; null = the default account. */
  account: string | null;
}

/** A launch policy with its flags (the operator default). */
export interface LaunchPolicyRef extends NamedRef {
  flags: LaunchPolicyFlags;
  /** Its owner enforces launch policies (`Launch_Policy.is_enabled`). */
  enforced: boolean;
}

export interface OperatorDefaults {
  location_sharing: number | null;
  has_group_chat: boolean | null;
  mytrips_display: boolean | null;
}

export interface TourCreateInput {
  mode: 'duplicate' | 'scratch';
  tour_operator: number;
  name: string;
  is_bespoke: boolean;
  trip_type: number;
  start_date?: string;
  start_timezone?: string;
  launch_policy?: { id: number };
  atag?: { id: number }[];
  source_id?: number;
}

// ---------------------------------------------------------------------------
// Itinerary (Phase 5) — the day→slot→activity tree for TOUR-type tours.
// Mirrors the dashboard read serializer; decimals arrive as strings.
// ---------------------------------------------------------------------------

/** A transport / timeline catalog reference (read: object; write: id). */
export interface CatalogRef {
  id: number;
  name: string | null;
}

export interface ItineraryDocument {
  id: number;
  type: number;
  name: string;
  sequence: number;
  link: string;
  url: string | null;
  size: number;
  content_type: string | null;
  content_hash: string | null;
  is_360_degree: boolean;
  offset_360_degree: number;
  deep_link: boolean;
}

export interface ItineraryActivityInfo {
  id: number;
  name: string;
  latitude: number | null;
  longitude: number | null;
  documents: unknown[];
}

export interface ItineraryActivity {
  id: number;
  sequence: number;
  activity: ItineraryActivityInfo | null;
  cost_min: string | null;
  cost_max: string | null;
  cost_prefix: string | null;
  cost_unit: string | null;
  currency: number | null;
  display_name: string | null;
  display_on_map: boolean;
  is_selected: boolean;
  next_transport: CatalogRef | null;
  ontrip_documents_type: number;
  payment_type: number;
  price_level: number | null;
  price_type: number;
  transport_text: string | null;
  booking_state: number | null;
  confirmation: string | null;
  documents: ItineraryDocument[];
}

/** pin_type is 'numbered' | 'icon' (dashboard/guest-app parity). */
export type PinType = 'numbered' | 'icon';

export interface ItinerarySlot {
  id: number;
  time: string; // "HH:MM:SS"
  title: string | null;
  subtitle: string | null;
  description: string | null;
  footer: string | null;
  type: number; // display_type
  timeline_type: CatalogRef | null;
  timeline_text: string | null;
  timeline_display: number; // 0 label / 1 time / 2 hide
  next_transport: CatalogRef | null;
  show_transport: boolean;
  transport_text: string | null;
  transport_per_activity: boolean;
  order_by: number;
  pin_type: PinType;
  pin_number_mode: 'sequential' | 'custom' | null;
  pin_custom_number: string | null;
  pin_icon_key: string | null;
  pin_icon_url: string | null;
  activities: ItineraryActivity[];
}

export interface ItineraryDay {
  id: number;
  sequence: number;
  day_span: number;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  footer: string | null;
  duration_display: string | null;
  image: unknown | null;
  kml_file: string | null;
  timeslots: ItinerarySlot[];
}

export interface Itinerary {
  id: number;
  cost: string | null;
  currency: number | null;
  cost_prefix: string | null;
  cost_unit: string | null;
  day_count: number;
  is_created: boolean;
  kml_file: string | null;
  branchio_link: string | null;
  days: ItineraryDay[];
  images: unknown[];
}

export interface TourDetail {
  id: number;
  /** Owning tenant; null for unassigned/orphaned tours. `TourDetailPage` renders
   *  a reduced view (no editor tabs) when this is null. */
  tenant: TenantRef | null;
  name: string;
  description: string | null;
  code: string | null;
  trip_type: number;
  trip_type_display: string;
  is_bespoke: boolean;
  use_as_template: boolean;
  /** The ops-facing name (never shown to guests). */
  internal_name: string | null;
  /** A guide group (staff chat group). */
  is_staff_chat_group: boolean;
  custom_id: string | null;
  /** Which pipelines consume the primary ID; null without one. */
  custom_id_used_by: ExtIdUsedBy | null;
  ext_id: string | null;
  ext_link: string | null;
  template_tour: NamedRef | null;
  additional_custom_ids: AdditionalCustomId[];
  cover_image: {
    id: number;
    image_name: string | null;
    url: string | null;
    /** Resize-service `{p,s}` chunks — pass to ImageUploadField `imageRef` to
     *  render a downsized preview (see the project image rule). */
    p: string | null;
    s: string | null;
  } | null;
  review: ExternalLink | null;
  photo_sharing: ExternalLink | null;
  positive_review_target: number;
  positive_review_target_display: string;
  positive_review_url: string | null;
  context: Record<string, unknown> | null;
  crosssells: NamedRef[];
  start_timezone: string | null;
  start_date: string | null;
  end_date: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  /** Whole seconds (derived from the itinerary, or set by hand for bus trips and
   *  exhibitions — `length_is_manual`). The write contract is the same int. */
  length: number | null;
  length_is_manual: boolean;
  has_departures: boolean;
  schedule: string | null;
  launch_policy: NamedRef | null;
  /** The EFFECTIVE policy (own, else the operator default) — not the operator default. */
  default_launch_policy: NamedRef | null;
  /** The effective policy's 12 flags; null when there is none. */
  launch_policy_flags: LaunchPolicyFlags | null;
  /** The operator's default policy (what the tour inherits without its own). */
  operator_default_launch_policy: LaunchPolicyRef | null;
  /** Whether the EFFECTIVE policy is enforced — its OWNER's `enable_launch_policies`
   *  (what `Launch_Policy.check_flag` reads); false = nothing is held back; null = no
   *  effective policy. */
  launch_policy_enforced: boolean | null;
  atag: NamedRef[];
  tags: NamedRef[];
  location_sharing: number;
  location_sharing_display: string;
  has_group_chat: boolean;
  exclude_from_chat: boolean;
  /** One-way in effect: it turns the guides & staff chat OFF for this tour only. */
  disable_internal_chat: boolean;
  /** The operator disabled internal chat for every tour (config). */
  internal_chat_disabled_by_operator: boolean;
  mytrips_display: boolean;
  /** null = the operator default. */
  show_all_users_to_guides_in_nonbespoke_tours: boolean | null;
  use_operator_defaults: boolean;
  /** The operator's default settings (config `defaults`) — what the app uses for location
   *  sharing and group chat while `use_operator_defaults` is on; null = none set up. */
  operator_defaults: OperatorDefaults | null;
  use_operator_pre_check_settings: boolean;
  allow_selfreg: boolean;
  /** null = the operator default; imports reset them. */
  selfreg_verify_email: boolean | null;
  selfreg_verify_phone: boolean | null;
  /** How many active tour-wide (undated: the master + custom) self-registration links
   *  exist — what a viewer may know; the links themselves need console_tours_manage. */
  selfreg_link_count: number;
  /** How many active date-specific links exist (mostly guides' per-booking links). */
  selfreg_dated_link_count: number;
  meeting_point: MeetingPoint | null;
  chat_priority: number;
  vehicle_route_set: NamedRef | null;
  itinerary: Itinerary | null;
  effective_display_currency: number;
  display_currency: number;
  user_stats: Record<string, number>;
  import_summary: ImportSummary;
  map_link: string | null;
  /** The tour's page in its operator's dashboard; null without an operator. */
  dashboard_url: string | null;
  // collections (Phase 3)
  departures: Departure[];
  tag_categories_own: TagCategoryM2M[];
  tag_categories_default: TagCategoryM2M[];
  /** The operator allows per-tour tag categories (else the PATCH drops them). */
  tag_categories_editable: boolean;
  pre_trip_items: PreTripItemRow[];
  /** The pre-trip items' "Visible to" targeting is in use. */
  enable_pre_trip_items_visibility: boolean;
  pre_check_settings: PreCheckSettings;
  pre_check_settings_own: PreCheckSettings;
  use_group_tour_pre_trip_items: boolean;
  is_deleted: boolean;
  created_ts: string | null;
  updated_ts: string | null;
}

export type DeletedFilter = 'exclude' | 'only' | 'include';

export interface ListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  trip_type?: number;
  is_bespoke?: boolean;
  use_as_template?: boolean;
  time?: 'past' | 'active' | 'upcoming' | 'nonbespoke';
  deleted?: DeletedFilter;
  /** URL-encoded JSON array of TourFilterNode objects (the QuerySearch wire AST,
   *  already stringified — it arrives via the grid `extra.filters` channel). */
  filters?: string;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.timeout) usp.set('timeout', params.timeout);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.trip_type !== undefined) usp.set('trip_type', String(params.trip_type));
  if (params.is_bespoke !== undefined) usp.set('is_bespoke', String(params.is_bespoke));
  if (params.use_as_template !== undefined) usp.set('use_as_template', String(params.use_as_template));
  if (params.time) usp.set('time', params.time);
  if (params.deleted) usp.set('deleted', params.deleted);
  if (params.filters) usp.set('filters', params.filters);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TourListItem>> {
  return apiGet<PaginatedResponse<TourListItem>>(`/tours/${toQuery(params)}`, extendedListConfig(params.timeout));
}

export function retrieve(id: number, opts: { deleted?: DeletedFilter } = {}): Promise<TourDetail> {
  const qs = opts.deleted ? `?deleted=${opts.deleted}` : '';
  return apiGet<TourDetail>(`/tours/${id}/${qs}`);
}

/** A PATCH body: any subset of editable fields + optional concurrency token. Values
 *  are passed straight to `update_from_apiv2_dict`. */
export type TourWriteInput = Record<string, unknown> & { expected_updated_ts?: string | null };

/** A map pin the PATCH could not apply (pins are best-effort, after the commit). */
export interface TourPinError {
  slot_id?: number | null;
  day_index?: number | null;
  slot_index?: number | null;
  message: string;
}

/** The PATCH response: the fresh detail plus the pins that could not be applied. */
export type TourPatchResult = TourDetail & { pin_errors?: TourPinError[] };

export function update(id: number, body: TourWriteInput): Promise<TourPatchResult> {
  return apiPatch<TourPatchResult>(`/tours/${id}/`, body);
}

/** A per-day KML file staged for upload, indexed by its position in the
 *  submitted `itinerary.days` list (the backend reads `kml_day_<dayIndex>`). */
export interface KmlUpload {
  dayIndex: number;
  file: File;
}

/** File parts a tour PATCH can carry alongside its JSON `_json` body. */
export interface TourUploadFiles {
  coverImage?: File;
  meetingPointPhoto?: File;
  kml?: KmlUpload[];
  /** The tour-level route file (the itinerary's `kml_file`, B6). */
  tourKml?: File;
}

export function hasUploads(files: TourUploadFiles): boolean {
  return Boolean(files.coverImage || files.meetingPointPhoto || files.tourKml || (files.kml && files.kml.length));
}

/** PATCH with file parts (multipart). The JSON body rides in `_json`; files are
 *  `cover_image` / `meeting_point_photo` / `kml_file` (the tour's route) /
 *  `kml_day_<dayIndex>` parts. Use `update` (plain JSON) when there are no files. */
export async function updateMultipart(
  id: number,
  body: TourWriteInput,
  files: TourUploadFiles,
): Promise<TourPatchResult> {
  const fd = new FormData();
  fd.append('_json', JSON.stringify(body));
  if (files.coverImage) fd.append('cover_image', files.coverImage);
  if (files.meetingPointPhoto) fd.append('meeting_point_photo', files.meetingPointPhoto);
  if (files.tourKml) fd.append('kml_file', files.tourKml);
  for (const { dayIndex, file } of files.kml ?? []) fd.append(`kml_day_${dayIndex}`, file);
  // Unset the default JSON content-type so the browser sets the multipart
  // boundary (console upload convention — see api/vehicles.ts).
  const r = await client.patch<TourPatchResult>(`/tours/${id}/`, fd, {
    headers: { 'Content-Type': undefined },
  });
  return r.data;
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/tours/${id}/`);
}

export function create(body: TourCreateInput): Promise<TourDetail> {
  return apiPost<TourDetail>('/tours/', body);
}

export function checkCustomId(
  tourOperatorId: number,
  value: string,
  excludeTourId?: number,
): Promise<{ available: boolean; value: string }> {
  const usp = new URLSearchParams({ tour_operator_id: String(tourOperatorId), value });
  if (excludeTourId !== undefined) usp.set('exclude', String(excludeTourId));
  return apiGet(`/tours/check_custom_id/?${usp.toString()}`);
}

export function picker(
  tourOperatorId: number,
  kind: PickerKind,
  q?: string,
  /** Optional tag-category code — narrows the `tags` picker to one category
   *  (sent as the `category` query param; ignored by other picker kinds). */
  category?: string,
  /** `tour_users`: the tour whose booked users are listed. */
  tourId?: number,
  /** At most this many rows (the server caps it at 200; default 50). */
  limit?: number,
  /** `activities`: also the franchise parent's (what a SAVED itinerary row may
   *  reference), each row with its `tenant`. */
  includeParent?: boolean,
): Promise<{ results: PickerOption[] }> {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q);
  if (category) usp.set('category', category);
  if (tourId !== undefined) usp.set('tour_id', String(tourId));
  if (limit !== undefined) usp.set('limit', String(limit));
  if (includeParent) usp.set('include_parent', '1');
  const qs = usp.toString();
  return apiGet(`/tour_operators/${tourOperatorId}/tour_pickers/${kind}/${qs ? `?${qs}` : ''}`);
}

/** The booking systems (and their accounts) the operator has configured. */
export function restechAccounts(tourOperatorId: number, q?: string): Promise<{ results: RestechAccountOption[] }> {
  const qs = q ? `?${new URLSearchParams({ q }).toString()}` : '';
  return apiGet(`/tour_operators/${tourOperatorId}/tour_pickers/restech_accounts/${qs}`);
}

export function createDepartureTag(
  tourOperatorId: number,
  time: string,
): Promise<{ id: number; name: string }> {
  return apiPost(`/tour_operators/${tourOperatorId}/departure_tags/`, { time });
}

// ---------------------------------------------------------------------------
// Per-activity on-trip documents (immediate-sync, keyed by User_Trip_Flow id).
// ---------------------------------------------------------------------------

export function listActivityDocuments(utfId: number): Promise<{ results: ItineraryDocument[] }> {
  return apiGet(`/tour_activities/${utfId}/documents/`);
}

export function createActivityDocumentLink(
  utfId: number,
  body: { name: string; url: string; deep_link?: boolean },
): Promise<ItineraryDocument> {
  return apiPost(`/tour_activities/${utfId}/documents/`, body);
}

export async function uploadActivityDocument(
  utfId: number,
  file: File,
  meta: { name?: string; deep_link?: boolean; is_360_degree?: boolean; offset_360_degree?: number } = {},
): Promise<ItineraryDocument> {
  const fd = new FormData();
  fd.append('_json', JSON.stringify(meta));
  fd.append('file', file);
  const r = await client.post<ItineraryDocument>(`/tour_activities/${utfId}/documents/`, fd, {
    headers: { 'Content-Type': undefined },
  });
  return r.data;
}

export function updateActivityDocument(
  utfId: number,
  docId: number,
  body: { name?: string; url?: string; deep_link?: boolean; is_360_degree?: boolean; offset_360_degree?: number },
): Promise<ItineraryDocument> {
  return apiPatch(`/tour_activities/${utfId}/documents/${docId}/`, body);
}

export function removeActivityDocument(utfId: number, docId: number): Promise<void> {
  return apiDelete(`/tour_activities/${utfId}/documents/${docId}/`);
}

export function reorderActivityDocuments(utfId: number, ids: number[]): Promise<void> {
  return apiPost(`/tour_activities/${utfId}/documents/reorder/`, { ids });
}

// ---------------------------------------------------------------------------
// Tour relations (P3): self-registration links, chat channels, the media gallery.
// ---------------------------------------------------------------------------

export type TourSelfregLink = components['schemas']['ConsoleTourSelfregLink'];
export type TourChatChannel = components['schemas']['ConsoleTourChatChannel'];
export type TourMediaItem = components['schemas']['ConsoleTourMediaItem'];

export type TourSelfregLinkList = components['schemas']['ConsoleTourSelfregLinkList'];
export type TourSelfregLinkPatch = components['schemas']['ConsoleTourSelfregLinkPatch'];
export type TourSelfregLinkError = components['schemas']['ConsoleTourSelfregLinkError'];
export type TourSelfregMasterChange = components['schemas']['ConsoleTourSelfregLinkMasterChange'];
export type TourSelfregRevokeResult = components['schemas']['ConsoleTourSelfregLinkRevoke'];
export type TourSelfregRestoreResult = components['schemas']['ConsoleTourSelfregLinkRestore'];

/** `GET /tours/{id}/selfreg_links/` query: `undated` (default) = the master + custom
 *  links; `dated` = the date-specific links (`date_from` / `date_to` inclusive). `q`: a
 *  full link URL or any part of a UUID. */
export interface SelfregLinkListParams {
  kind?: 'undated' | 'dated';
  status?: 'active' | 'revoked' | 'all';
  q?: string;
  date_from?: string;
  date_to?: string;
  ordering?: 'master' | 'upcoming' | 'created_ts' | '-created_ts' | 'date' | '-date';
  page?: number;
  page_size?: number;
}

export function listSelfregLinks(tourId: number, params: SelfregLinkListParams = {}): Promise<TourSelfregLinkList> {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const qs = usp.toString();
  return apiGet(`/tours/${tourId}/selfreg_links/${qs ? `?${qs}` : ''}`);
}

export function createSelfregLink(tourId: number): Promise<TourSelfregLink> {
  return apiPost(`/tours/${tourId}/selfreg_links/`, {});
}

/** The per-link settings, saved at once (not part of the tour draft). */
export function updateSelfregLink(tourId: number, linkId: string, patch: TourSelfregLinkPatch): Promise<TourSelfregLink> {
  return apiPatch(`/tours/${tourId}/selfreg_links/${linkId}/`, patch);
}

/** An active custom undated link becomes the master; the master becomes custom. */
export function makeSelfregLinkMaster(tourId: number, linkId: string): Promise<TourSelfregMasterChange> {
  return apiPost(`/tours/${tourId}/selfreg_links/${linkId}/make-master/`, {});
}

/** Revoking the master needs `successor`: a custom link's UUID, or `"new"`. */
export function revokeSelfregLink(
  tourId: number,
  linkId: string,
  body: { successor?: string; reason?: string } = {},
): Promise<TourSelfregRevokeResult> {
  return apiPost(`/tours/${tourId}/selfreg_links/${linkId}/revoke/`, body);
}

export function restoreSelfregLink(tourId: number, linkId: string): Promise<TourSelfregRestoreResult> {
  return apiPost(`/tours/${tourId}/selfreg_links/${linkId}/restore/`, {});
}

export function listChatChannels(tourId: number, page = 1, pageSize = 25): Promise<PaginatedResponse<TourChatChannel>> {
  return apiGet(`/tours/${tourId}/chat_channels/?page=${page}&page_size=${pageSize}`);
}

export function unarchiveChatChannel(tourId: number, channelId: number): Promise<{ channel: TourChatChannel; queued: number }> {
  return apiPost(`/tours/${tourId}/chat_channels/${channelId}/unarchive/`, {});
}

export function listTourMedia(tourId: number, page = 1, pageSize = 25): Promise<PaginatedResponse<TourMediaItem>> {
  return apiGet(`/tours/${tourId}/media/?page=${page}&page_size=${pageSize}`);
}
